import * as React from "react";
import {ReactElement} from "react";
import {useStore, useStoreValue} from "react-context-hook";
import Icon from "../Icon";
import {getConfiguration, getText} from "../../services/configuration.service";
import {useEffect} from "react";
import {getDurationLabel} from "../../services/label.service";
import {IBookingCalendarSearchCity} from "../index";

const SelectDuration = (): ReactElement => {
    const bookingEngineData = useStoreValue("bookingEngineData");
    const isLocationContext = bookingEngineData?.product?.location;
    const basePriceAvailability = ((bookingEngineData || {}).product || {}).basePriceAvailability;
    const availabilities = (bookingEngineData || {}).availabilities;
    const cities = (bookingEngineData || {}).cities;
    const search = (bookingEngineData || {}).search;
    const selectedDepartureCity = useStoreValue("selectedDepartureCity");
    const selectedCapacity = useStoreValue("selectedCapacity");
    const [selectedDuration, setSelectedDuration] = useStore("selectedDuration");
    const selectedDepartureCityLabel = (cities && selectedDepartureCity && cities[selectedDepartureCity.code]) || getText("booking.loading.label");

    let durations: string[] = availabilities && selectedDepartureCity && availabilities[selectedDepartureCity.code] &&
        (isLocationContext ? Object.keys(availabilities[selectedDepartureCity.code][selectedCapacity.code]) : Object.keys(availabilities[selectedDepartureCity.code]));

    const selectLabel = (selectedDuration && getDurationLabel(selectedDuration.code)) || getText("booking.loading.label");
    if (durations) {
        durations = durations
            .sort((a: string, b: string) => Number(a.split("-")[0]) - Number(b.split("-")[0]));
    }

    useEffect(() => {
        if (!isLocationContext) {
            const selectedCityIsBaseAvail = ((basePriceAvailability || {}).departureCity || {}).code === (selectedDepartureCity || {}).code;
            const queryParameters = getConfiguration().queryParameters;
            if (!selectedDuration || selectedCityIsBaseAvail) {
                if (bookingEngineData && basePriceAvailability) {
                    let cityCode = basePriceAvailability.departureCity.code;
                    let durationCode = basePriceAvailability.nbDays + "-" + basePriceAvailability.nbNights;
                    // select from hash url
                    if (queryParameters && queryParameters.city && availabilities[queryParameters.city]) {
                        cityCode = queryParameters.city;
                    }
                    // Select duration of the base availability of the product
                    const cityAvails = availabilities[cityCode];
                    if (queryParameters && queryParameters.duration && cityAvails[queryParameters.duration]) {
                        durationCode = queryParameters.duration;
                    } else if (cityCode !== basePriceAvailability.departureCity.code) {
                        const selectedDepartureCityData = search.departureCity.filter((departureCity: any) => departureCity.code === cityCode)[0];
                        const lowestPriceDuration = getDurationLowestPrice(selectedDepartureCityData);
                        if (lowestPriceDuration) {
                            durationCode = lowestPriceDuration.code;
                        }
                    }
                    const durationValue = cityAvails[durationCode];
                    setSelectedDuration({code: durationCode, value: durationValue});
                }
            } else if (selectedDepartureCity) {
                const cityCode = selectedDepartureCity.code;
                let queryParametersCityCode;

                if (queryParameters && queryParameters.city && availabilities[queryParameters.city]) {
                    queryParametersCityCode = queryParameters.city;
                }
                const selectedDepartureCityData = search.departureCity.filter((departureCity: any) => departureCity.code === cityCode)[0];
                // Select duration with the lowest price
                if (selectedDepartureCityData && queryParametersCityCode !== cityCode) {
                    const lowestPriceDuration = getDurationLowestPrice(selectedDepartureCityData);
                    if (lowestPriceDuration) {
                        setSelectedDuration({
                            code: lowestPriceDuration.code,
                            value: availabilities[cityCode][lowestPriceDuration.code]
                        });
                    }
                } else if (queryParametersCityCode === cityCode) {
                    const cityAvails = availabilities[cityCode];
                    if (queryParameters && queryParameters.duration && cityAvails[queryParameters.duration]) {
                        const durationCode = queryParameters.duration;
                        const durationValue = cityAvails[durationCode];
                        setSelectedDuration({code: durationCode, value: durationValue});
                    }
                }
            }
        }

    }, [bookingEngineData, selectedDepartureCity, isLocationContext]);

    useEffect(() => {
        if (isLocationContext && basePriceAvailability && selectedDepartureCity) {
            if (!selectedDuration) { // first arrival on page
                const defaultDurationCode = basePriceAvailability.nbDays + "-" + basePriceAvailability.nbNights;
                const durationValueValue = availabilities[selectedDepartureCity.code][selectedCapacity.code][defaultDurationCode];
                setSelectedDuration({
                    code: defaultDurationCode,
                    value: durationValueValue
                });
            } else if (selectedCapacity) { // after re-selecting capacity
                let durationCode;
                // check if already selectedDuration.code exists with the new capacity
                if (availabilities[selectedDepartureCity.code][selectedCapacity.code][selectedDuration.code]) {
                    durationCode = selectedDuration.code;
                } else { // take the first duration of the new capacity
                    durationCode = Object.keys(availabilities[selectedDepartureCity.code][selectedCapacity.code])[0];
                }

                const durationValueValue = availabilities[selectedDepartureCity.code][selectedCapacity.code][durationCode];
                setSelectedDuration({
                    code: durationCode,
                    value: durationValueValue
                });
            }
        }

    }, [availabilities, basePriceAvailability, search, isLocationContext, selectedCapacity]);

    const getDurationLowestPrice = (selectedDepartureCityData: IBookingCalendarSearchCity) => {
        if (selectedDepartureCityData) {
            const lowestPrice = selectedDepartureCityData.lowestPrice;
            const durationStays = selectedDepartureCityData?.durationStay;
            if (durationStays?.length > 0 && lowestPrice) {
                const lowestDurationStays = durationStays.filter((durationStay: { code: string, lowestPriceByDuration: number }) =>
                    durationStay.lowestPriceByDuration === lowestPrice);
                if (lowestDurationStays.length > 0) {
                    return lowestDurationStays[0];
                }
            }
        }
    };

    const renderLegend = () => {

        const selectLabel: string = getText("booking.select.label");
        const fromLabel = getText("booking.select.departure.from.city");
        return (
            <>
                {selectLabel} <span className="option"> ({fromLabel}
                    <strong>{selectedDepartureCityLabel}</strong>)</span>
            </>
        );
    };

    const selectDuration = (duration: string) => {
        if (selectedDuration.code !== duration) {
            setSelectedDuration({
                code: duration,
                value: isLocationContext ? availabilities[selectedDepartureCity.code][selectedCapacity.code][duration] :
                    availabilities[selectedDepartureCity.code][duration]
            });
        }
    };

    return (
        <div className="booking-item js-switch-active">
            <div className="label">{getText("booking.select.duration.label")}</div>
            <div className={"elem-custom-select"}>
                <div className="custom-select-mask">
                    <div className="custom-select-input">
                        <div className="custom-select-input-txt">
                            <Icon icon={"l"} addClass={"location-icon"}/>
                            <div className="label-style">{selectLabel}</div>
                        </div>
                        <div className="custom-select-input-button">{getText("booking.select.change")}</div>
                    </div>
                </div>
                <div className="custom-select-panel d-none">

                    <div className="title-block">
                        <div className="label-block">
                            <div className="label">{getText("booking.select.durations.available")}</div>
                            <div className="legend">{renderLegend()}</div>
                        </div>
                        <div className="icon-wrap btn-close-custom-select">
                            <Icon icon={"x"}/>
                        </div>
                    </div>
                    <div className="custom-select-panel-content">
                        {!durations &&
                            <div
                                className="panel-content-item placeholder-style">{getText("booking.loading.label")}</div>
                        }
                        {durations && durations.map((duration: string) =>
                            <div
                                key={duration}
                                onClick={() => selectDuration(duration)}
                                className={`panel-content-item ${duration === selectedDuration?.code ? "selected" : ""}`}
                            >
                                {getDurationLabel(duration)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SelectDuration;
